/**
 * VideoPlayer.js
 * 
 * Created on Sept 2013
 *
 * @author Javier Quevedo-Fernandez for Ranj
 **/

import EventDispatcher from "/lib/html5-software-library/src/ranj/util/EventDispatcher";
import { isNull } from "/lib/html5-software-library/src/ranj/util/TypeUtil";
import VideoPlayerDispenserHelper from "./VideoPlayerDispenserHelper";
import VideoPlayerEvent from "./VideoPlayerEvent";
import PausedOverlay from "./PausedOverlay";

export default class VideoPlayer extends EventDispatcher {
    constructor(overlayID) {
        super();

        this._areSubtitlesVisible = false;
        this._videoWrapperDOMElement = VideoPlayerDispenserHelper.getVideoWrapper(overlayID);
        this._player = VideoPlayerDispenserHelper.getVideoPlayer(overlayID);

        this._pausedOverlay = VideoPlayerDispenserHelper.getPausedOverlay();
        this._autoRetry = true;

        this.addEventListeners(this._player);
    }

    destroy() {
        this._pausedOverlay.hide();
        this.stop();
        this.setVisible(false);

        this.removeEventListeners(this._player);

        // If you do not want to see the last frame of the previous video when reusing a video player, the source must be set to a black video upon destroying the video player
        //var source = [{src: "assets/scenario_test_movie/movie/briefing_black.mp4", type: 'video/mp4'}, {src: "assets/scenario_test_movie/movie/briefing_black.mp4", type:'video/ogg'}];
        //this._player.setSrc(source);

        this._player = null;
        this._videoWrapperDOMElement = null;
    }

    addEventListeners(player) {
        this._onVideoEndBound = this._onVideoEnd.bind(this);
        this._onVideoErrorBound = this._onVideoError.bind(this);
        player.on('ended', this._onVideoEndBound);
        player.on('error', this._onVideoErrorBound);

        this._onPausedOverlayClickedBound = this._onPausedOverlayClicked.bind(this);
        this._pausedOverlay.dispatcher.addEventListener(PausedOverlay.CLICK, this._onPausedOverlayClickedBound);
        this._onPausedOverlaySkipBound = this._onPausedOverlaySkip.bind(this);
        this._pausedOverlay.dispatcher.addEventListener(PausedOverlay.SKIP, this._onPausedOverlaySkipBound);
    }

    removeEventListeners(player) {
        if (!isNull(player)) {
            player.off('ended', this._onVideoEndBound);
            player.off('error', this._onVideoErrorBound);
        }

        this._pausedOverlay.dispatcher.removeEventListener(PausedOverlay.CLICK, this._onPausedOverlayClickedBound);
        this._pausedOverlay.dispatcher.removeEventListener(PausedOverlay.SKIP, this._onPausedOverlaySkipBound);

        this._onVideoEndBound = null;
    }

    /**
     * Loads a video into this video player.
     * @return {Promise<videojs.VideoJsPlayer>} A promise that will be resolved when the video player is ready.
     */
    load(videoUrl, thumbnailUrl, subtitlesUrl, showSubtitles) {

        // Remember passed in arguments for reloading the video if the initial loading fails
        this._videoUrl = videoUrl;
        this._thumbnailUrl = thumbnailUrl;
        this._subtitlesUrl = subtitlesUrl;
        this._showSubtitles = showSubtitles;

        return new Promise(resolve => {
            this._player.poster(thumbnailUrl);
            this._player.src(videoUrl);

            this._player.addRemoteTextTrack({
                src: subtitlesUrl,
                default: true // If this track should default to on or off.
            }, false); // second parameter (false) = manual cleanup ("if set to false, the TextTrack will be removed on a source change")

            this.toggleSubtitles(showSubtitles);

            this._player.ready(() => {
                this.dispatchEvent(VideoPlayerEvent.PLAYBACK_READY);
                resolve(this._player);
            });
        })
    }

    reload() {
        this.load(this._videoUrl, this._thumbnailUrl, this._subtitlesUrl, this._showSubtitles);
    }

    toggleSubtitles(areVisible) {
        this._areSubtitlesVisible = isNull(areVisible) ? !this._areSubtitlesVisible : areVisible;
        let tracks = this._player.remoteTextTracks();
        for (let i = 0; i < tracks.length; i++) {
            const track = tracks[i];
            track.mode = this._areSubtitlesVisible ? 'showing' : 'hidden'; // https://html.spec.whatwg.org/multipage/media.html#texttrackmode
        }
    }

    setVisible(visible) {
        if (!isNull(visible) && visible) {
            this._videoWrapperDOMElement.style.display = "block";
        }
        else {
            this._videoWrapperDOMElement.style.display = "none";
        }
    }

    play() {
        this.setVisible(true);
        this._pausedOverlay.hide();

        return this._player.play();
    }

    pause() {
        this._pausedOverlay.show();
        this._player.pause();
    }

    resume() {
        this._pausedOverlay.hide();
        return this._player.play();
    }

    stop() {
        this._player.pause();
    }

    replay() {
        this.stop();
        this.play();
    }

    skip() {
        this.stop();
        this._onVideoEnd();
    }

    _onVideoEnd(evt) {
        this.dispatchEvent(VideoPlayerEvent.PLAYBACK_FINISHED);
    }

    _onVideoError(evt) {
        console.error(`Encountered a video error.`, evt);

        this.dispatchEvent(VideoPlayerEvent.PLAYBACK_ERROR);

        if (!this.autoRetry) {
            return;
        }

        console.error(`Attempting to reload video playlist in ${VideoPlayer.RETRY_INTERVAL} milliseconds.`);

        setTimeout(() => {
            this.reload();
        }, VideoPlayer.RETRY_INTERVAL);
    }

    _onPausedOverlayClicked(evt) {
        this.resume();
    }

    _onPausedOverlaySkip(evt) {
        this.skip();
    }

    get autoRetry() {
        return this._autoRetry;
    }

    set autoRetry(value) {
        this._autoRetry = value;
    }
}

VideoPlayer.RETRY_INTERVAL = 3000;
VideoPlayer.INTRODUCTION_VIDEO = "Introduction";
VideoPlayer.COMM_PER_CH1_INTRO = "CommunityPerspective1intro";
VideoPlayer.COMM_PER_CH1_OUTRO = "CommunityPerspective1outro";
VideoPlayer.COMM_PER_CH2_INTRO = "CommunityPerspective2intro";
VideoPlayer.COMM_PER_CH2_OUTRO = "CommunityPerspective2outro";
VideoPlayer.COMM_PER_CH3_INTRO = "CommunityPerspective3intro";
VideoPlayer.COMM_PER_CH3_OUTRO = "CommunityPerspective3outro";
VideoPlayer.NARR_OUT_BAD = "Narrative Outcome BadDummy";
VideoPlayer.NARR_OUT_GOOD = "Narrative Outcome Good";
VideoPlayer.NARR_OUT_SUBOPTIMAL = "Narrative Outcome Suboptimal";